import { Card, Flex, type TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/lib/table/interface';
import { Table } from 'components/table/Table';
import type { MetersByMeterModelsFilter, PopulatedCombinedMeter } from 'lib/services';
import { useMemo, type FC } from 'react';
import { useParams } from 'react-router-dom';
import { useMetersByModelIdWithPagination } from './hooks/useMetersFilterWithPagination';
import { MetersFilter } from './components/MetersFilter';
import { getTableColumns } from './utils/getTableColumns';
import { useMetersByModelId } from '@/api/hooks/meter-models/useMetersByModelId';

export const MetersTab: FC = () => {
  const { id } = useParams<{ id: string }>();
  const modelId = Number(id);

  const { pagination, filter, setFilterAndPaginationState } = useMetersByModelIdWithPagination();
  const { data, isLoading } = useMetersByModelId(modelId, { ...filter, ...pagination });

  const meters = data?.result ?? [];
  const total = data?.count ?? 0;

  const handleTableChange = (
    changedPagination: TablePaginationConfig,
    filters: Partial<MetersByMeterModelsFilter>,
    sorter: SorterResult<PopulatedCombinedMeter>,
  ) => {
    const changedFilter = {
      ...(sorter.columnKey && sorter.order ? { sort: `${sorter.columnKey}:${sorter.order}` } : { sort: null }),
      ...filters,
    };

    setFilterAndPaginationState(changedFilter, changedPagination);
  };

  const columns = useMemo(() => getTableColumns(filter), [filter]);

  return (
    <Flex vertical gap={8}>
      <Card>
        <MetersFilter />
      </Card>

      <Card>
        <Table<PopulatedCombinedMeter>
          columns={columns}
          dataSource={meters}
          loading={isLoading}
          pagination={{ ...pagination, total }}
          rowKey={meter => meter._id}
          scroll={{ x: true }}
          onChange={(changedPagination, filters, sorter) => {
            handleTableChange(changedPagination, filters, Array.isArray(sorter) ? sorter[0] : sorter);
          }}
        />
      </Card>
    </Flex>
  );
};
